import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { focusRing } from '../lib/control';
import { cn } from '../lib/cn';

export const buttonVariants = cva(
  `inline-flex shrink-0 items-center justify-center gap-2 max-w-full text-body font-medium whitespace-normal transition-colors disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-danger aria-invalid:ring-danger [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 ${focusRing}`,
  {
    variants: {
      variant: {
        // Portal 1a: actions use the same rounded rectangular control shape.
        default: 'rounded-control bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'rounded-control bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'rounded-control border border-border-control bg-surface text-foreground hover:bg-accent hover:text-accent-foreground',
        secondary: 'rounded-control bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'rounded-control hover:bg-accent hover:text-accent-foreground',
        link: 'rounded-control text-link underline-offset-4 hover:underline',
      },
      size: {
        default: 'min-h-(--control-h) px-4 py-2 has-[>svg]:px-3',
        sm: 'min-h-(--control-h-sm) gap-1.5 px-3 py-1 text-body-sm has-[>svg]:px-2.5',
        lg: 'min-h-(--control-h-lg) px-6 py-3 has-[>svg]:px-4',
        icon: 'size-(--control-h)',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Renderiza o filho como elemento raiz (ex.: `<a>`). */
    asChild?: boolean;
  };

export function Button({ className, variant, size, asChild = false, type, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      type={asChild ? type : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}
